import type { ActiveContext, DocumentChunk, DocumentContextChunk } from '../types';

const DEFAULT_TARGET_CHARS = 1_200;
const DEFAULT_OVERLAP_CHARS = 200;
const DEFAULT_MAX_CHARS = 12_000;
const DEFAULT_MAX_CHUNKS = 8;

const STOP_WORDS = new Set([
  'about', 'after', 'again', 'also', 'and', 'are', 'been', 'break', 'build',
  'can', 'counts', 'csv', 'data', 'document', 'emails', 'for', 'from', 'give',
  'help', 'into', 'lease', 'list', 'make', 'more', 'next', 'option', 'please',
  'property', 'put', 'query', 'report', 'rollup', 'show', 'summarize', 'table',
  'that', 'the', 'them', 'these', 'this', 'units', 'with', 'workbook', 'xlsx',
  'yes', 'you', 'your',
]);

export interface RetrievalDocument {
  fileName: string;
  chunks: DocumentChunk[];
  contentType?: string;
  content?: string;
}

function normalizeWhitespace(value: string): string {
  return value.replace(/\r/g, '').replace(/[ \t]+/g, ' ').trim();
}

function tokenize(value: string): string[] {
  return normalizeWhitespace(value)
    .toLowerCase()
    .split(/[^a-z0-9]+/i)
    .filter((token) => token.length > 2 && !STOP_WORDS.has(token));
}

export function queryLooksLikeShortDocumentFollowUp(query: string): boolean {
  const normalized = normalizeWhitespace(query).toLowerCase();
  if (!normalized) return false;

  const shortFollowUpPatterns = [
    /^(yes|ok|okay|sure|please)\b/,
    /\bdo option \d+\b/,
    /^option \d+\b/,
    /\bput that in a table\b/,
    /\bbreak (it|that) out\b/,
    /\bby property\b/,
    /\bexport (it|that)\b/,
  ];

  return (
    normalized.split(/\s+/).length <= 8 &&
    shortFollowUpPatterns.some((pattern) => pattern.test(normalized))
  );
}

export function queryLooksLikeDocumentComparison(query: string, activeContext?: ActiveContext): boolean {
  const normalized = [
    query,
    activeContext?.selectedOptionHint,
    activeContext?.lastTask,
  ]
    .filter((value): value is string => typeof value === 'string' && value.trim().length > 0)
    .join(' ')
    .toLowerCase();

  if (!normalized) return false;

  const comparisonSignals = [
    /\bcompare\b/,
    /\bcomparison\b/,
    /\bdifferences?\b/,
    /\bwhat changed\b/,
    /\bchanges?\s+between\b/,
    /\bversus\b/,
    /\bvs\.?\b/,
    /\bredline\b/,
    /\breconcile\b/,
    /\bconflicts?\b/,
    /\bmismatches?\b/,
    /\bside[\s-]?by[\s-]?side\b/,
  ];

  return comparisonSignals.some((pattern) => pattern.test(normalized));
}

export function looksLikeSpreadsheetDocument(fileName: string, contentType?: string): boolean {
  const normalizedName = normalizeWhitespace(fileName).toLowerCase();
  const normalizedType = normalizeWhitespace(contentType || '').toLowerCase();

  return (
    normalizedName.endsWith('.xlsx') ||
    normalizedName.endsWith('.xls') ||
    normalizedName.endsWith('.csv') ||
    normalizedType === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
    normalizedType === 'application/vnd.ms-excel' ||
    normalizedType === 'text/csv'
  );
}

export function queryNeedsFullDocumentContext(query: string, activeContext?: ActiveContext): boolean {
  const normalized = [
    query,
    activeContext?.selectedOptionHint,
    activeContext?.lastTask,
  ]
    .filter((value): value is string => typeof value === 'string' && value.trim().length > 0)
    .join(' ')
    .toLowerCase();

  if (!normalized) return false;

  if (queryLooksLikeDocumentComparison(query, activeContext)) return true;

  const broadRollupSignals = [
    /\ball\b/,
    /\bevery\b/,
    /\beach\b/,
    /\bentire\b/,
    /\bfull\b/,
    /\bcomplete\b/,
    /\breturn\b/,
    /\brollup\b/,
    /\bcounts?\b/,
    /\bsummary\b/,
    /\bsummarize\b/,
    /\brefrigerant\b/,
    /\brefridgerant\b/,
    /\bbuilding\b/,
    /\borganize\b/,
    /\borganized\b/,
    /\bgroup(?:ed)?\b/,
    /\bsort(?:ed)?\b/,
    /\bby property\b/,
    /\bby age\b/,
    /\bbuckets?\b/,
    /\btiers?\b/,
    /\bcsv\b/,
    /\bexport\b/,
    /\boption\s*\d+\b/,
    /\b\d+\s+rtus?\b/,
  ];

  return broadRollupSignals.some((pattern) => pattern.test(normalized));
}

export function buildDocumentChunks(
  text: string,
  fileName: string,
  options: { targetChars?: number; overlapChars?: number } = {}
): DocumentChunk[] {
  const normalized = normalizeWhitespace(text);
  if (!normalized) return [];

  const targetChars = options.targetChars ?? DEFAULT_TARGET_CHARS;
  const overlapChars = options.overlapChars ?? DEFAULT_OVERLAP_CHARS;
  const chunks: DocumentChunk[] = [];

  let start = 0;
  let chunkIndex = 0;
  while (start < normalized.length) {
    let end = Math.min(start + targetChars, normalized.length);
    if (end < normalized.length) {
      const softBreak = Math.max(
        normalized.lastIndexOf('\n', end),
        normalized.lastIndexOf('. ', end),
        normalized.lastIndexOf(', ', end)
      );
      if (softBreak > start + Math.floor(targetChars * 0.6)) {
        end = softBreak + 1;
      }
    }

    const content = normalized.slice(start, end).trim();
    if (!content) break;

    chunks.push({
      id: `${fileName}:${chunkIndex}`,
      chunkIndex,
      totalChunks: 0,
      content,
    });

    chunkIndex += 1;
    if (end >= normalized.length) break;
    start = Math.max(end - overlapChars, start + 1);
  }

  return chunks.map((chunk) => ({ ...chunk, totalChunks: chunks.length }));
}

function calculateChunkOverlap(left: string, right: string): number {
  const maxOverlap = Math.min(left.length, right.length, DEFAULT_OVERLAP_CHARS * 3);

  for (let overlap = maxOverlap; overlap > 0; overlap -= 1) {
    if (left.slice(-overlap) === right.slice(0, overlap)) {
      return overlap;
    }
  }

  return 0;
}

export function rebuildDocumentTextFromChunks(chunks: DocumentChunk[]): string {
  if (!Array.isArray(chunks) || chunks.length === 0) return '';

  const ordered = [...chunks].sort((a, b) => a.chunkIndex - b.chunkIndex);
  let rebuilt = ordered[0]?.content || '';

  for (let index = 1; index < ordered.length; index += 1) {
    const nextContent = ordered[index]?.content || '';
    if (!nextContent) continue;

    const overlap = calculateChunkOverlap(rebuilt, nextContent);
    rebuilt += nextContent.slice(overlap);
  }

  return rebuilt;
}

function buildQueryTerms(query: string, activeContext?: ActiveContext): string[] {
  const terms = new Set<string>(tokenize(query));

  if (activeContext?.selectedOptionHint) {
    tokenize(activeContext.selectedOptionHint).forEach((token) => terms.add(token));
  }
  if (activeContext?.lastTask) {
    tokenize(activeContext.lastTask).forEach((token) => terms.add(token));
  }
  if (activeContext?.documentNames) {
    activeContext.documentNames.forEach((name) => tokenize(name).forEach((token) => terms.add(token)));
  }

  return Array.from(terms);
}

function scoreChunk(chunk: DocumentChunk, queryTerms: string[], activeContext?: ActiveContext): number {
  const haystack = chunk.content.toLowerCase();
  let score = 0;

  for (const term of queryTerms) {
    if (!term) continue;
    const occurrences = haystack.split(term.toLowerCase()).length - 1;
    if (occurrences > 0) {
      score += occurrences * 3;
    }
  }

  if (activeContext?.selectedOptionHint) {
    const optionTerms = tokenize(activeContext.selectedOptionHint);
    for (const term of optionTerms) {
      if (haystack.includes(term)) score += 2;
    }
  }

  return score;
}

export function selectRelevantDocumentChunks(
  documents: RetrievalDocument[],
  query: string,
  activeContext?: ActiveContext,
  options: { maxChars?: number; maxChunks?: number } = {}
): DocumentContextChunk[] {
  const maxChars = options.maxChars ?? DEFAULT_MAX_CHARS;
  const maxChunks = options.maxChunks ?? DEFAULT_MAX_CHUNKS;

  if (documents.length === 0) return [];

  const shortFollowUp = queryLooksLikeShortDocumentFollowUp(query);
  const flattened = documents.flatMap((document) =>
    document.chunks.map((chunk) => ({
      fileName: document.fileName,
      chunk,
    }))
  );

  let ranked: Array<{ fileName: string; chunk: DocumentChunk; relevanceScore: number }>;
  if (shortFollowUp || buildQueryTerms(query, activeContext).length === 0) {
    ranked = flattened.map(({ fileName, chunk }, index) => ({
      fileName,
      chunk,
      relevanceScore: 100 - index,
    }));
  } else {
    const queryTerms = buildQueryTerms(query, activeContext);
    ranked = flattened
      .map(({ fileName, chunk }) => ({
        fileName,
        chunk,
        relevanceScore: scoreChunk(chunk, queryTerms, activeContext),
      }))
      .sort((a, b) => b.relevanceScore - a.relevanceScore || a.chunk.chunkIndex - b.chunk.chunkIndex);
  }

  const selected: DocumentContextChunk[] = [];
  const selectedKeys = new Set<string>();
  let totalChars = 0;

  const addCandidate = (candidate: { fileName: string; chunk: DocumentChunk; relevanceScore: number }): boolean => {
    if (selected.length >= maxChunks) return false;
    const key = `${candidate.fileName}:${candidate.chunk.chunkIndex}`;
    if (selectedKeys.has(key)) return true;

    const contentLength = candidate.chunk.content.length;
    if (selected.length > 0 && totalChars + contentLength > maxChars) return false;

    selected.push({
      fileName: candidate.fileName,
      content: candidate.chunk.content,
      chunkIndex: candidate.chunk.chunkIndex,
      totalChunks: candidate.chunk.totalChunks,
      relevanceScore: candidate.relevanceScore,
    });
    selectedKeys.add(key);
    totalChars += contentLength;
    return true;
  };

  if (queryLooksLikeDocumentComparison(query, activeContext) && documents.length > 1 && maxChunks > 1) {
    const topCandidateByDocument = new Map<string, { fileName: string; chunk: DocumentChunk; relevanceScore: number }>();
    for (const candidate of ranked) {
      if (!topCandidateByDocument.has(candidate.fileName)) {
        topCandidateByDocument.set(candidate.fileName, candidate);
      }
    }

    for (const candidate of topCandidateByDocument.values()) {
      if (!addCandidate(candidate)) break;
    }
  }

  for (const candidate of ranked) {
    if (!addCandidate(candidate)) break;
  }

  return selected;
}
